import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { Task } from "model/activitiesModel";
import { ServerTag } from "model/activitiesModel";
import { ProcessModelType } from "model/processModel";
import { Priority } from "hooks/usePriorities";
import { SortCriterion } from "common/utilities";
import { getTranslation } from "common";
import MyActivitiesMobileHeader from "./MyActivitiesMobileHeader";
import MobileTabBar from "./MobileTabBar";
import MobileTaskList from "./MobileTaskList";
import MobileSplitLayout from "./MobileSplitLayout";

type TabValue = "todo" | "doing";

interface MyActivitiesMobileViewProps {
  todoTasks: Task[];
  doingTasks: Task[];
  searchText: string;
  setSearchText: (v: string) => void;
  searchTags: number[];
  setSearchTags: (tags: number[]) => void;
  selectedPriorities: string[];
  setSelectedPriorities: (priorities: string[]) => void;
  selectedModels: number[];
  setSelectedModels: (models: number[]) => void;
  selectedUserDataKey: string | null;
  handleSelectUserDataKey: (key: string | null) => void;
  setSortCriteria: (criteria: SortCriterion) => void;
  handleClearFilters: () => void;
  processModels: ProcessModelType[];
  tagList: ServerTag[];
  priorities: Priority[];
  userDataKeys: string[];
  setStartProcess: (open: boolean) => void;
  setDetail: (idprocesstoken: number) => void;
  liveProcessCanUse: boolean;
  bypassSubscription: boolean;
  organizationId?: number;
  organizationName?: string;
  /** When true, render the landscape 2-column split layout instead of the tabbed portrait layout. */
  isLandscape: boolean;
  translations: Record<string, string>;
  /** Forwarded to the task cards so the user can move tasks forward from the card itself. */
  onNextStep?: (task: Task) => void | Promise<void>;
  /** Forwarded to the task cards so the user can move a "doing" task back to "todo". */
  onBackStep?: (task: Task) => void | Promise<void>;
}

/**
 * Entry point of the mobile-first MyActivities page.
 * - Portrait: header + filters + tab bar (Da Fare | In Corso) + scrollable list.
 * - Landscape: header + filters + 2-column split (Da Fare | In Corso).
 * No drag&drop on mobile; transitions are handled via the task detail drawer.
 */
const MyActivitiesMobileView: React.FC<MyActivitiesMobileViewProps> = ({
  todoTasks,
  doingTasks,
  searchText,
  setSearchText,
  searchTags,
  setSearchTags,
  selectedPriorities,
  setSelectedPriorities,
  selectedModels,
  setSelectedModels,
  selectedUserDataKey,
  handleSelectUserDataKey,
  setSortCriteria,
  handleClearFilters,
  processModels,
  tagList,
  priorities,
  userDataKeys,
  setStartProcess,
  setDetail,
  liveProcessCanUse,
  bypassSubscription,
  organizationId,
  organizationName,
  isLandscape,
  translations,
  onNextStep,
  onBackStep,
}) => {
  const [tab, setTab] = useState<TabValue>("todo");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilterCount = useMemo(
    () =>
      (searchText ? 1 : 0) +
      searchTags.length +
      selectedPriorities.length +
      selectedModels.length +
      (selectedUserDataKey ? 1 : 0),
    [searchText, searchTags, selectedPriorities, selectedModels, selectedUserDataKey],
  );

  const todoEmpty = getTranslation(translations, "generic.no_tasks_to_do.caption");
  const doingEmpty = getTranslation(translations, "generic.no_ongoing_activity.caption");

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <MyActivitiesMobileHeader
        searchText={searchText}
        setSearchText={setSearchText}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
        filtersOpen={filtersOpen}
        setStartProcess={setStartProcess}
        liveProcessCanUse={liveProcessCanUse}
        bypassSubscription={bypassSubscription}
        organizationId={organizationId}
        organizationName={organizationName}
        translations={translations}
        searchTags={searchTags}
        setSearchTags={setSearchTags}
        selectedPriorities={selectedPriorities}
        setSelectedPriorities={setSelectedPriorities}
        selectedModels={selectedModels}
        setSelectedModels={setSelectedModels}
        selectedUserDataKey={selectedUserDataKey}
        handleSelectUserDataKey={handleSelectUserDataKey}
        setSortCriteria={setSortCriteria}
        handleClearFilters={handleClearFilters}
        processModels={processModels}
        tagList={tagList}
        priorities={priorities}
        userDataKeys={userDataKeys}
        activeFilterCount={activeFilterCount}
      />

      {isLandscape ? (
        <MobileSplitLayout
          todoTasks={todoTasks}
          doingTasks={doingTasks}
          onTaskClick={setDetail}
          onNextStep={onNextStep}
          onBackStep={onBackStep}
          todoEmptyMessage={todoEmpty}
          doingEmptyMessage={doingEmpty}
          translations={translations}
        />
      ) : (
        <>
          <MobileTabBar
            value={tab}
            onChange={setTab}
            todoCount={todoTasks.length}
            doingCount={doingTasks.length}
            translations={translations}
          />
          {tab === "todo" ? (
            <MobileTaskList
              tasks={todoTasks}
              emptyMessage={todoEmpty}
              emptyAccent="#54bdc1"
              onTaskClick={setDetail}
              onNextStep={onNextStep}
              onBackStep={onBackStep}
              translations={translations}
            />
          ) : (
            <MobileTaskList
              tasks={doingTasks}
              emptyMessage={doingEmpty}
              emptyAccent="#ed6c02"
              onTaskClick={setDetail}
              onNextStep={onNextStep}
              onBackStep={onBackStep}
              translations={translations}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default MyActivitiesMobileView;